import React, { useState, useEffect } from 'react';
import { 
  X, CheckCircle, Crown, Zap, Shield, Star, Globe, Server, Wifi, Activity, 
  CalendarClock, User, Mail, Key, Lock, Eye, EyeOff, AlertCircle, ArrowRight,
  AlertTriangle, Hash, KeyRound, Layers, Database, Boxes, Monitor, BarChart3, Gauge,
  Play, Plug, WifiIcon, Cpu, Settings, Briefcase
} from 'lucide-react';

const API_BASE = "/api";

function PricingModal({ isOpen, onClose, currentUser, onPlanChange, darkMode = false }) {
  const greenColor = '#95DE1A';
  const redColor = '#FD0E0B';
  const blueColor = '#0b7dc3';
  
  const gradientGreenStart = '#85C34A';
  const gradientGreenMiddle = '#95DE1A';
  const gradientGreenEnd = '#A5E84A';
  
  const gradientRedStart = '#E60B08';
  const gradientRedMiddle = '#FD0E0B';
  const gradientRedEnd = '#FF3B30';

  const peachColor = '#fe2a04';
  const peachLight = '#f3776020';
  const peachLighter = '#f3776010';
  const peachSoft = '#f3776040';

  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processingPlan, setProcessingPlan] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordStep, setPasswordStep] = useState(1);
  const [passwordEmail, setPasswordEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPlansAndSubscription();
      fetchUserDetails();
    }
  }, [isOpen]);

  const fetchPlansAndSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const plansResponse = await fetch(`${API_BASE}/subscription/plans`);
      const plansData = await plansResponse.json();
      setPlans(plansData.plans);

      const subResponse = await fetch(`${API_BASE}/subscription/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const subData = await subResponse.json();
      
      let userStats = {};
      try {
        const statsResponse = await fetch(`${API_BASE}/user/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsResponse.ok) {
          userStats = await statsResponse.json();
        }
      } catch (err) {
        console.error('Error fetching user stats:', err);
      }
      
      const subscription = {
        ...subData.subscription,
        websiteCount: userStats.websiteCount || 0,
        serviceCount: userStats.serviceCount || 0,
        syntheticTestCount: userStats.syntheticTestCount || 0,
        apiCount: userStats.apiCount || 0,
        portCount: userStats.portCount || 0,
        pingCount: userStats.pingCount || 0,
      };
      
      setCurrentSubscription(subscription);
      setSelectedPlan(subscription.planType);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          setUserDetails(userData);
          setPasswordEmail(userData.email);
        } catch (e) {
          console.error('Error parsing user from localStorage:', e);
        }
      }
      
      if (currentUser) {
        setUserDetails(currentUser);
        setPasswordEmail(currentUser.email);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleUpgrade = async (planType) => {
    setProcessingPlan(planType);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/subscription/freemius/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planType,
          duration: billingCycle
        })
      });

      const data = await response.json();

      if (response.ok && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        alert(data.error || data.warning || 'Failed to start checkout. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setProcessingPlan('');
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert('Subscription cancelled successfully. You will receive an email confirmation.');
        if (onPlanChange) {
          onPlanChange({ subscriptionStatus: 'cancelled' });
        }
        setShowCancelConfirmModal(false);
        onClose();
      } else {
        if (data.error && data.error.includes('No active subscription found')) {
          alert('Your account does not have an active subscription in our payment system. If you manually created this account, please contact support to cancel it.');
        } else {
          alert(data.error || 'Failed to cancel subscription');
        }
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Network error');
    } finally {
      setCancelling(false);
      setShowCancelConfirmModal(false);
    }
  };

  const handleSendVerificationCode = async (e) => {
    e.preventDefault();

    if (!passwordEmail.trim()) {
      setPasswordError('Email is required');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/auth/send-password-change-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: passwordEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordStep(2);
        setPasswordError('');
      } else {
        setPasswordError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Send code error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setPasswordError('Please enter a valid 6-digit code');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/auth/verify-password-change-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: passwordEmail,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordStep(3);
        setPasswordError('');
      } else {
        setPasswordError(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Verify code error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setPasswordError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/auth/change-password-with-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: passwordEmail,
          code: verificationCode,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordSuccess(true);
        setTimeout(() => {
          setShowPasswordModal(false);
          resetPasswordState();
        }, 2000);
      } else {
        setPasswordError(data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/auth/send-password-change-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: passwordEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('New code sent to your email');
      } else {
        setPasswordError(data.error || 'Failed to resend code');
      }
    } catch (error) {
      setPasswordError('Network error');
    } finally {
      setIsResending(false);
    }
  };

  const resetPasswordState = () => {
    setPasswordStep(1);
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
    setPasswordSuccess(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    resetPasswordState();
  };

  if (!isOpen) return null;

  const planOrder = ['free', 'starter', 'professional', 'enterprise'];
  const orderedPlans = planOrder
    .map(key => ({ key, ...plans[key] }))
    .filter(plan => plan.name);

  const getPlanIcon = (planKey) => {
    switch (planKey) {
      case 'free':
        return <Star className="h-6 w-6" />;
      case 'starter':
        return <Zap className="h-6 w-6" />;
      case 'professional':
        return <Crown className="h-6 w-6" />;
      case 'enterprise':
        return <Shield className="h-6 w-6" />;
      default:
        return <Star className="h-6 w-6" />;
    }
  };

  const getPlanGradient = (planKey) => {
    switch (planKey) {
      case 'free':
        return `linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)`;
      case 'starter':
        return `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`;
      case 'professional':
        return `linear-gradient(135deg, ${gradientRedStart} 0%, ${gradientRedMiddle} 50%, ${gradientRedEnd} 100%)`;
      case 'enterprise':
        return `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`;
      default:
        return `linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)`;
    }
  };

  const planLevels = { free: 0, starter: 1, professional: 2, enterprise: 3 };

  const getButtonConfig = (planKey, isCurrentPlan) => {
    if (isCurrentPlan) {
      return {
        text: 'Current Plan',
        style: {
          background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`,
          color: 'white',
          cursor: 'not-allowed'
        },
        disabled: true
      };
    }

    const currentLevel = currentSubscription ? planLevels[currentSubscription.planType] : -1;
    const selectedLevel = planLevels[planKey];

    if (!currentSubscription || currentSubscription.planType === 'free') {
      if (planKey === 'free') {
        return {
          text: 'Downgrade to Free',
          style: {
            background: `linear-gradient(135deg, ${gradientRedStart} 0%, ${gradientRedMiddle} 50%, ${gradientRedEnd} 100%)`,
            color: 'white'
          },
          disabled: false
        };
      } else {
        return {
          text: 'Start Trial',
          style: {
            background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`,
            color: 'white'
          },
          disabled: false
        };
      }
    }

    if (selectedLevel > currentLevel) {
      return {
        text: 'Upgrade',
        style: {
          background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`,
          color: 'white'
        },
        disabled: false
      };
    } else if (selectedLevel < currentLevel) {
      return {
        text: 'Downgrade',
        style: {
          background: `linear-gradient(135deg, ${gradientRedStart} 0%, ${gradientRedMiddle} 50%, ${gradientRedEnd} 100%)`,
          color: 'white'
        },
        disabled: false
      };
    } else {
      return {
        text: 'Current Plan',
        style: {
          background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`,
          color: 'white',
          cursor: 'not-allowed'
        },
        disabled: true
      };
    }
  };

  const getPrice = (plan, cycle) => {
    if (plan.price === 0) return '$0';
    const price = cycle === 'yearly' ? plan.price * 10 : plan.price;
    return `$${price.toFixed(2)}`;
  };

  const getSavings = (plan) => {
    if (plan.price === 0) return null;
    const monthlyCost = plan.price * 12;
    const yearlyCost = plan.price * 10;
    const savings = Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
    return savings;
  };

  const formatInterval = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h`;
    } else if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}min`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const daysLeft = (dateString) => {
    if (!dateString) return null;
    const now = new Date();
    const end = new Date(dateString);
    const diff = end - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getPlanDisplayName = (planType) => {
    switch (planType) {
      case 'free': return 'Free Plan';
      case 'starter': return 'Starter Plan';
      case 'professional': return 'Professional Plan';
      case 'enterprise': return 'Enterprise Plan';
      default: return 'Current Plan';
    }
  };

  const getUserDisplayName = () => {
    if (userDetails?.firstName || userDetails?.lastName) {
      return `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim();
    }
    
    if (currentUser?.firstName || currentUser?.lastName) {
      return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
    }
    
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.firstName || user.lastName) {
          return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }
        return user.email?.split('@')[0] || 'User';
      } catch (e) {
        return 'User';
      }
    }
    
    return 'User';
  };

  const getUserEmail = () => {
    if (userDetails?.email) return userDetails.email;
    if (currentUser?.email) return currentUser.email;
    
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.email || '';
      } catch (e) {
        return '';
      }
    }
    
    return '';
  };

  const totalMonitors = currentSubscription?.websiteCount || 0;
  const totalMonitorsLimit = currentSubscription?.maxWebsites || 0;
  const serviceCount = currentSubscription?.serviceCount || 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`flex items-center justify-between p-8 border-b sticky top-0 z-10 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-red-300 bg-white'}`}>
          <div>
            <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Choose Your Plan</h2>
            <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upgrade or downgrade at any time</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <div className="p-8">
          {/* BİRLEŞİK PLAN VE KULLANIM KUTUSU */}
          <div className="rounded-3xl mb-8 overflow-hidden" style={{ backgroundColor: peachColor }}>
            {/* Plan Bilgileri - Üst Kısım */}
            <div className="p-6 border-b border-white/20">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center">
                    <Crown className="h-8 w-8" style={{ color: greenColor }} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {getPlanDisplayName(currentSubscription?.planType)}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mt-1">
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4 text-white/80" />
                        <span className="text-white font-medium">
                          {getUserDisplayName()}
                        </span>
                      </div>
                      <span className="hidden sm:inline text-white/60">•</span>
                      <div className="flex items-center space-x-1">
                        <Mail className="h-4 w-4 text-white/80" />
                        <span className="text-white">{getUserEmail()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={() => {
                      setPasswordEmail(getUserEmail());
                      setShowPasswordModal(true);
                    }}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 rounded-xl transition-colors shadow-sm"
                  >
                    <Key className="h-4 w-4" style={{ color: redColor }} />
                    <span className="text-sm font-medium text-gray-700">Change Password</span>
                  </button>
                  {currentSubscription && currentSubscription.planType !== 'free' && (
                    <button
                      onClick={() => setShowCancelConfirmModal(true)}
                      className="flex items-center justify-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 rounded-xl transition-colors shadow-sm"
                    >
                      <AlertTriangle className="h-4 w-4" style={{ color: redColor }} />
                      <span className="text-sm font-medium" style={{ color: redColor }}>Cancel Subscription</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Current Usage */}
            {currentSubscription && (
              <div className={`p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
                  <Layers className="h-5 w-5 mr-2" style={{ color: peachColor }} />
                  Plan Usage
                </h4>
                
                <div className="space-y-4">
                  {/* Total Monitors */}
                  <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center mb-3">
                      <Monitor className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                      <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Total Monitors</span>
                      <span className={`ml-2 text-sm font-medium ${totalMonitors > totalMonitorsLimit ? 'text-red-600' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({totalMonitors}/{totalMonitorsLimit})
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <Briefcase className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Service</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {serviceCount}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>API</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.apiCount || 0}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <Plug className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Port</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.portCount || 0}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <WifiIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ping</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.pingCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Limits per Service */}
                  <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center mb-3">
                      <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                      <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Limits per Service</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>API per Service</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.maxApisPerWebsite || 0}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <Plug className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Port per Service</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.maxPortsPerWebsite || 0}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <WifiIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ping per Service</span>
                        </div>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {currentSubscription.maxPingsPerWebsite || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Synthetic Tests */}
                  <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Play className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                        <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Synthetic Tests</span>
                      </div>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {currentSubscription.syntheticTestCount || 0} / {currentSubscription.maxSyntheticTests || 0}
                      </span>
                    </div>
                  </div>

                  {/* Check Interval */}
                  <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Gauge className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                        <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Check Interval</span>
                      </div>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatInterval(currentSubscription.maxCheckInterval)}
                      </span>
                    </div>
                  </div>

                  {/* Plan Status */}
                  <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Shield className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                        <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Plan Status</span>
                      </div>
                      <span className={`text-sm font-semibold capitalize ${
                        currentSubscription.subscriptionStatus === 'active' 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {currentSubscription.subscriptionStatus}
                      </span>
                    </div>
                  </div>

                  {/* Expiry Date */}
                  {currentSubscription.planEndDate && (
                    <div className={`border rounded-xl p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CalendarClock className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                          <span className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Expiry Date</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatDate(currentSubscription.planEndDate)}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            daysLeft(currentSubscription.planEndDate) > 7 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : daysLeft(currentSubscription.planEndDate) > 0
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}>
                            {daysLeft(currentSubscription.planEndDate) > 0 
                              ? `${daysLeft(currentSubscription.planEndDate)}d left`
                              : daysLeft(currentSubscription.planEndDate) === 0
                              ? 'Today'
                              : 'Expired'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Billing Cycle Seçici */}
          <div className="flex items-center justify-center mb-8">
            <div className={`rounded-2xl p-1 flex ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? `${darkMode ? 'bg-gray-600 shadow-md text-white' : 'bg-white shadow-md text-gray-900'}`
                    : `${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 relative ${
                  billingCycle === 'yearly'
                    ? `${darkMode ? 'bg-gray-600 shadow-md text-white' : 'bg-white shadow-md text-gray-900'}`
                    : `${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                Yearly
                <span 
                  className="absolute -top-2 -right-2 text-white text-xs px-2 py-1 rounded-full"
                  style={{ backgroundColor: greenColor }}
                >
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          {/* Plan Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {orderedPlans.map((plan) => {
              const isCurrentPlan = currentSubscription?.planType === plan.key;
              const isPopular = plan.key === 'starter';
              const buttonConfig = getButtonConfig(plan.key, isCurrentPlan);
              
              return (
                <div
                  key={plan.key}
                  className={`relative rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                    isPopular ? 'ring-2 scale-105' : ''
                  } ${isCurrentPlan ? 'ring-2' : ''} border-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}
                  style={{ borderColor: redColor }}
                >
                  {(isPopular || isCurrentPlan) && (
                    <div className="absolute -top-4 left-0 right-0 flex justify-center gap-2 px-4">
                      {isPopular && (
                        <span 
                          className="text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap"
                          style={{ 
                            background: `linear-gradient(135deg, ${gradientRedStart} 0%, ${gradientRedMiddle} 50%, ${gradientRedEnd} 100%)`
                          }}
                        >
                          Most Popular
                        </span>
                      )}
                      {isCurrentPlan && (
                        <span 
                          className="text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap"
                          style={{ 
                            background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)`
                          }}
                        >
                          Current Plan
                        </span>
                      )}
                    </div>
                  )}

                  {plan.key === 'free' && !isCurrentPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span 
                        className="text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap"
                        style={{ backgroundColor: greenColor }}
                      >
                        14-day trial
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div 
                      className="w-12 h-12 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white"
                      style={{ background: getPlanGradient(plan.key) }}
                    >
                      {getPlanIcon(plan.key)}
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                    <div className="mb-2">
                      <span className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {getPrice(plan, billingCycle)}
                      </span>
                      {plan.price > 0 && (
                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} ml-1`}>
                          /{billingCycle === 'yearly' ? 'year' : 'month'}
                        </span>
                      )}
                    </div>
                    {billingCycle === 'yearly' && plan.price > 0 && (
                      <p className="text-sm font-medium" style={{ color: greenColor }}>
                        Save {getSavings(plan)}% annually
                      </p>
                    )}
                    {plan.key === 'free' && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>14-day free trial, no credit card required</p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features?.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: redColor }} />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={processingPlan === plan.key || buttonConfig.disabled}
                    className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={buttonConfig.style}
                  >
                    {processingPlan === plan.key ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      buttonConfig.text
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* İPTAL ONAY MODALI */}
      {showCancelConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
          <div className={`rounded-3xl shadow-2xl w-full max-w-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6" style={{ backgroundColor: peachColor }}>
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-white" />
                <h3 className="text-2xl font-bold text-white">Cancel Subscription</h3>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Are you sure you want to cancel your subscription? 
                <span className="font-semibold block mt-2" style={{ color: redColor }}>
                  You will continue to have access until the end of your billing period.
                </span>
              </p>
              
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This action cannot be undone. After cancellation, your account will be downgraded to the Free plan at the end of your current billing cycle.
              </p>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCancelConfirmModal(false)}
                  className={`flex-1 px-4 py-3 font-semibold rounded-xl transition-colors ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  No, Keep Plan
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex-1 px-4 py-3 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center"
                  style={{ backgroundColor: peachColor }}
                >
                  {cancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ŞİFRE DEĞİŞTİRME MODALI */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className={`rounded-3xl shadow-2xl w-full max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {passwordStep === 1 ? (
              <>
                <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: peachColor }}>Change Password</h2>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>We'll send a verification code to your email</p>
                  </div>
                  <button onClick={handleClosePasswordModal} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                </div>

                <form onSubmit={handleSendVerificationCode} className="p-8 space-y-6">
                  {passwordError && (
                    <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-300 font-medium">{passwordError}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email Address *</label>
                    <div className="relative">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="email"
                        value={passwordEmail}
                        onChange={(e) => setPasswordEmail(e.target.value)}
                        className={`w-full pl-12 pr-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                        placeholder="your@email.com"
                        required
                        readOnly={!!getUserEmail()}
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border" style={{ backgroundColor: peachSoft, borderColor: peachColor }}>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} flex items-start`}>
                      <Mail className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" style={{ color: peachColor }} />
                      <span>
                        <strong style={{ color: peachColor }}>Verification required:</strong> We'll send a 6-digit code to <span className="font-semibold" style={{ color: peachColor }}>{passwordEmail}</span>.
                      </span>
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="w-full text-white font-medium px-6 py-3 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                    style={{ backgroundColor: peachColor }}
                  >
                    {passwordLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending Code...</span>
                      </div>
                    ) : 'Send Verification Code'}
                  </button>
                </form>
              </>
            ) : passwordStep === 2 ? (
              <>
                <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: peachColor }}>Verify Code</h2>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Enter the 6-digit code sent to<br />
                      <span className="font-medium text-gray-700 dark:text-gray-300">{passwordEmail}</span>
                    </p>
                  </div>
                  <button onClick={handleClosePasswordModal} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                </div>

                <div className="p-8 space-y-6">
                  {passwordError && (
                    <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-300 font-medium">{passwordError}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Verification Code *</label>
                    <div className="relative">
                      <KeyRound className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setVerificationCode(val);
                          if (passwordError) setPasswordError('');
                        }}
                        placeholder="123456"
                        maxLength="6"
                        className={`w-full pl-12 pr-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                        required
                      />
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Enter the 6-digit code sent to your email</p>
                  </div>

                  <button
                    onClick={handleVerifyCode}
                    disabled={passwordLoading || verificationCode.length !== 6}
                    className="w-full text-white font-medium px-6 py-3 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                    style={{ backgroundColor: peachColor }}
                  >
                    {passwordLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Verifying...</span>
                      </div>
                    ) : 'Verify Code'}
                  </button>

                  <div className="flex items-center justify-between pt-2">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isResending}
                      className="text-sm font-medium hover:underline disabled:opacity-50"
                      style={{ color: peachColor }}
                    >
                      {isResending ? 'Sending...' : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPasswordStep(1)}
                      className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      ← Back
                    </button>
                  </div>
                </div>
              </>
            ) : passwordStep === 3 ? (
              <>
                <div className={`flex items-center justify-between p-8 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: peachColor }}>Set New Password</h2>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enter your new password</p>
                  </div>
                  <button onClick={handleClosePasswordModal} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                </div>

                <form onSubmit={handleChangePassword} className="p-8 space-y-6">
                  {passwordError && (
                    <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-300 font-medium">{passwordError}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>New Password *</label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={`w-full pl-12 pr-12 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Minimum 8 characters, include uppercase, lowercase, and numbers</p>
                  </div>

                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Confirm New Password *</label>
                    <div className="relative">
                      <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className={`w-full pl-12 pr-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FD0E0B] ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="w-full text-white font-medium px-6 py-3 rounded-2xl shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                    style={{ backgroundColor: peachColor }}
                  >
                    {passwordLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Updating...</span>
                      </div>
                    ) : 'Change Password'}
                  </button>
                </form>
              </>
            ) : passwordSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Password Changed!</h4>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your password has been successfully changed.</p>
                <button
                  onClick={handleClosePasswordModal}
                  className={`mt-6 px-6 py-2 rounded-xl ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Close
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingModal;